import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { CircleNotch } from "@phosphor-icons/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select"
import { TYPE_OPTIONS } from "../schemas/contact-form-schema"
import { useCreateContact } from "../hooks/useContactMutations"

// Schema for create contact - requires at least one of family_name, email, or phone
const createContactSchema = z
  .object({
    given_name: z.string().optional(),
    family_name: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
    type: z.string().optional(),
  })
  .refine(
    (data) => data.family_name || data.email || data.phone,
    {
      message: "At least one of last name, email, or phone is required",
      path: ["family_name"], // Show error on family_name field
    }
  )

type CreateContactFormData = z.infer<typeof createContactSchema>

interface CreateContactModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateContactModal({ open, onOpenChange }: CreateContactModalProps) {
  const navigate = useNavigate()
  const createContact = useCreateContact()
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateContactFormData>({
    resolver: zodResolver(createContactSchema),
    defaultValues: {
      given_name: "",
      family_name: "",
      email: "",
      phone: "",
      type: undefined,
    },
  })

  const handleClose = () => {
    reset()
    setError(null)
    onOpenChange(false)
  }

  const onSubmit = async (data: CreateContactFormData) => {
    setError(null)
    try {
      const result = await createContact.mutateAsync(data)
      handleClose()
      // Navigate to the newly created contact
      if (result?.data?._id) {
        navigate(`/contacts/${result.data._id}`)
      }
    } catch (err: any) {
      setError(err.message || "Failed to create contact")
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Contact</DialogTitle>
          <DialogDescription>
            Add a new contact to your database. At least one of last name, email, or phone is required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="given_name" className="text-sm">
                First Name
              </Label>
              <Input
                id="given_name"
                {...register("given_name")}
                placeholder="First name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="family_name" className="text-sm">
                Last Name
              </Label>
              <Input
                id="family_name"
                {...register("family_name")}
                placeholder="Last name"
              />
              {errors.family_name?.message && (
                <p className="text-xs text-danger">{String(errors.family_name.message)}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              {...register("email")}
              placeholder="email@example.com"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label htmlFor="phone" className="text-sm">
              Phone
            </Label>
            <Input
              id="phone"
              {...register("phone")}
              placeholder="(555) 555-5555"
            />
          </div>

          {/* Type */}
          <div className="space-y-1.5">
            <Label htmlFor="type" className="text-sm">
              Type
            </Label>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || "__none__"}
                  onValueChange={(val) =>
                    field.onChange(val === "__none__" ? undefined : val)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {TYPE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Error message */}
          {error && (
            <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded-md">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createContact.isPending}>
              {createContact.isPending && (
                <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Contact
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
